import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Table,
  LayerCard,
  Empty,
  Badge,
  Button,
  Dialog,
  InputGroup,
  useKumoToastManager,
} from '@cloudflare/kumo'
import {
  UsersIcon,
  HouseIcon,
  DownloadSimpleIcon,
  MagnifyingGlassIcon,
  CameraIcon,
  PencilSimpleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useHasRole } from '../auth/useRoles'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FacePhotoCard } from '../components/face-photo/face-photo-card'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Company, Coworker, CoworkerStatus } from '@hub-wifi/shared'

// Mirrors the PrinterStatusBadge pattern — single source of truth for
// enum -> i18n label.
const STATUS_KEY: Record<CoworkerStatus, string> = {
  active: 'coworkers.status.active',
  inactive: 'coworkers.status.inactive',
}

type CoworkerTab = 'all' | CoworkerStatus

/** Escapes a single CSV field — quotes it when it contains a comma, quote, or newline. */
function escapeCsvField(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function toCsv(rows: string[][]): string {
  return rows.map((row) => row.map(escapeCsvField).join(',')).join('\r\n')
}

export function CoworkersPage() {
  const { getAccessTokenSilently } = useAuth0()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isAdmin = useHasRole('admin')
  const toasts = useKumoToastManager()
  const [coworkers, setCoworkers] = useState<Coworker[]>([])
  const [companies, setCompanies] = useState<Map<string, string>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<CoworkerTab>('all')
  const [query, setQuery] = useState('')
  const [statusToggleTarget, setStatusToggleTarget] = useState<Coworker | null>(null)
  const [statusToggleBusy, setStatusToggleBusy] = useState(false)
  const [photoTarget, setPhotoTarget] = useState<Coworker | null>(null)

  const loadCoworkers = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const [data, companyList] = await Promise.all([
        apiRequest<Coworker[]>('/api/coworkers', { token }),
        apiRequest<Company[]>('/api/companies', { token }),
      ])
      setCoworkers(data)
      setCompanies(new Map(companyList.map((c) => [c.id, c.name])))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void loadCoworkers()
  }, [loadCoworkers])

  async function confirmStatusToggle() {
    if (!statusToggleTarget) return
    const isActive = statusToggleTarget.status === 'active'
    setStatusToggleBusy(true)
    try {
      const token = await getAccessTokenSilently()
      if (isActive) {
        await apiRequest(`/api/coworkers/${statusToggleTarget.id}`, { method: 'DELETE', token })
      } else {
        await apiRequest(`/api/coworkers/${statusToggleTarget.id}`, {
          method: 'PUT',
          token,
          body: { status: 'active' },
        })
      }
      toasts.add({ variant: 'success', title: t('coworkers.statusUpdateSuccess') })
      setStatusToggleTarget(null)
      await loadCoworkers()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    } finally {
      setStatusToggleBusy(false)
    }
  }

  const companyNameFor = useCallback(
    (coworker: Coworker): string =>
      coworker.companyId === null
        ? t('common.b2c')
        : (companies.get(coworker.companyId) ?? coworker.companyId),
    [companies, t],
  )

  const normalizedQuery = query.trim().toLowerCase()

  const filteredCoworkers = coworkers
    .filter((coworker) => activeTab === 'all' || coworker.status === activeTab)
    .filter((coworker) => {
      if (normalizedQuery === '') return true
      return (
        coworker.name.toLowerCase().includes(normalizedQuery) ||
        coworker.email.toLowerCase().includes(normalizedQuery) ||
        companyNameFor(coworker).toLowerCase().includes(normalizedQuery)
      )
    })

  function handleExportCsv() {
    const header = [
      t('coworkers.name'),
      t('coworkers.email'),
      t('coworkers.company'),
      t('coworkers.status'),
    ]
    const rows = filteredCoworkers.map((coworker) => [
      coworker.name,
      coworker.email,
      companyNameFor(coworker),
      t(STATUS_KEY[coworker.status]),
    ])
    const csv = toCsv([header, ...rows])
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const date = new Date().toISOString().slice(0, 10)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `coworkers-${date}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('coworkers.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('coworkers.title')}
        description={t('coworkers.description')}
        tabs={[
          { label: t('coworkers.tabs.all'), value: 'all' },
          { label: t('coworkers.tabs.active'), value: 'active' },
          { label: t('coworkers.tabs.inactive'), value: 'inactive' },
        ]}
        defaultTab={activeTab}
        onValueChange={(value) => setActiveTab(value as CoworkerTab)}
      >
        <Button
          variant="outline"
          size="sm"
          icon={<DownloadSimpleIcon size={16} />}
          onClick={handleExportCsv}
        >
          {t('coworkers.exportCsv')}
        </Button>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs">
          <InputGroup.Addon>
            <MagnifyingGlassIcon />
          </InputGroup.Addon>
          <InputGroup.Input
            value={query}
            placeholder={t('coworkers.searchPlaceholder')}
            aria-label={t('coworkers.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query !== '' && (
            <InputGroup.Addon align="end" className="pr-1">
              <InputGroup.Button
                shape="square"
                icon={XIcon}
                aria-label={t('common.clear')}
                onClick={() => setQuery('')}
              />
            </InputGroup.Addon>
          )}
        </InputGroup>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && coworkers.length === 0 && (
        <Empty
          icon={<UsersIcon size={48} className="text-kumo-inactive" />}
          title={t('coworkers.empty')}
        />
      )}

      {!loading && !error && coworkers.length > 0 && filteredCoworkers.length === 0 && (
        <Empty
          icon={<UsersIcon size={48} className="text-kumo-inactive" />}
          title={t('common.noResultsForFilter')}
        />
      )}

      {!loading && !error && filteredCoworkers.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('coworkers.name')}</Table.Head>
                    <Table.Head>{t('coworkers.email')}</Table.Head>
                    <Table.Head>{t('coworkers.status')}</Table.Head>
                    <Table.Head>{t('coworkers.company')}</Table.Head>
                    {isAdmin && <Table.Head>{t('coworkers.actions')}</Table.Head>}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredCoworkers.map((coworker) => (
                    <Table.Row key={coworker.id}>
                      <Table.Cell>{coworker.name}</Table.Cell>
                      <Table.Cell>{coworker.email}</Table.Cell>
                      <Table.Cell>
                        <Badge variant={coworker.status === 'active' ? 'primary' : 'secondary'}>
                          {t(STATUS_KEY[coworker.status])}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{companyNameFor(coworker)}</Table.Cell>
                      {isAdmin && (
                        <Table.Cell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<PencilSimpleIcon size={16} />}
                              aria-label={t('coworkers.edit.action')}
                              onClick={() => navigate(`/coworkers/${coworker.id}/edit`)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<CameraIcon size={16} />}
                              aria-label={t('coworkers.facePhoto.action')}
                              onClick={() => setPhotoTarget(coworker)}
                            />
                            <Button
                              variant={
                                coworker.status === 'active' ? 'secondary-destructive' : 'secondary'
                              }
                              size="sm"
                              onClick={() => setStatusToggleTarget(coworker)}
                            >
                              {coworker.status === 'active'
                                ? t('coworkers.deactivate')
                                : t('coworkers.activate')}
                            </Button>
                          </div>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      <ConfirmDialog
        open={!!statusToggleTarget}
        onOpenChange={(next) => {
          if (!next) setStatusToggleTarget(null)
        }}
        title={
          statusToggleTarget?.status === 'active'
            ? t('coworkers.confirmDeactivate')
            : t('coworkers.confirmActivate')
        }
        confirmLabel={
          statusToggleTarget?.status === 'active'
            ? t('coworkers.deactivate')
            : t('coworkers.activate')
        }
        variant={statusToggleTarget?.status === 'active' ? 'destructive' : 'primary'}
        loading={statusToggleBusy}
        onConfirm={() => void confirmStatusToggle()}
      />

      {photoTarget && (
        <Dialog.Root
          open={Boolean(photoTarget)}
          onOpenChange={(open) => {
            if (!open) setPhotoTarget(null)
          }}
        >
          <Dialog size="base" className="p-6">
            <div className="mb-4 flex items-center justify-between gap-4">
              <Dialog.Title className="text-lg font-semibold">{t('facePhoto.title')}</Dialog.Title>
              <Dialog.Close
                render={(props) => (
                  <Button
                    {...props}
                    variant="secondary"
                    shape="square"
                    size="sm"
                    icon={<XIcon size={16} />}
                    aria-label={t('common.close')}
                  />
                )}
              />
            </div>
            <FacePhotoCard coworkerId={photoTarget.id} />
          </Dialog>
        </Dialog.Root>
      )}
    </div>
  )
}
